import defaultTo from 'lodash/defaultTo';
import capitalize from 'lodash/capitalize';
import { PricingPlanStatus } from '@atlassian/cssxp-typescommon';
import { getBillEstimateV2 } from '@atlassian/cssxp-proxies';
import { PricingPlanType } from '@atlassian/cssxp-ccp-types';
import { PricingPlan, InvoiceGroup, Offering } from '@css-xp/graphtypes/src/generated-types-new';
import { Maybe } from '@css-xp/graphtypes';
import { isOfferingIncluded } from '@css-xp/ppc-utils';
import { ProductOffering } from '@css-xp/graphtypes/src/generated-types-sxp';

export interface OfferingSelectItem {
  label: string;
  value: string;
}

const PricingPlanTypeDisplay = {
  DEFAULT_PRICING: 'DEFAULT_PRICING',
  ADVANTAGE_PRICING: 'ADVANTAGE_PRICING',
};

export const getPricingPlanDescription = (pricingPlan?: PricingPlan) => {
  const noDataContent = '-';

  if (pricingPlan) {
    const description = pricingPlan?.description ?? noDataContent;
    const type = pricingPlan?.pricingPlanType
      ? ' | ' + capitalize(pricingPlan?.pricingPlanType).replace(/_/g, ' ')
      : '';
    const status = pricingPlan?.status ? ' | ' + capitalize(pricingPlan?.status) : '';

    return description + type + status;
  }

  return noDataContent;
};

export const getPricingEligibility = ({
  invoiceGroup,
  billToPartyPricingEligibilty,
}: {
  invoiceGroup?: InvoiceGroup | null;
  billToPartyPricingEligibilty?: string | null;
}): PricingPlanType | null => {
  if (invoiceGroup) {
    if (invoiceGroup?.shipToPriceEligibility) {
      return invoiceGroup?.shipToPriceEligibility?.toUpperCase() as PricingPlanType;
    } else if (billToPartyPricingEligibilty) {
      return billToPartyPricingEligibilty?.toUpperCase() as PricingPlanType;
    } else {
      return PricingPlanType.COMMERCIAL;
    }
  }
  return null;
};
/**
 * Returns the plan having default pricing plan type
 * In the fallback returns the plan having advantage pricing plan type
 * @param pricingPlans
 */
const getAppropriatePricingPlanBasedOnPlanType = (pricingPlans: Maybe<Maybe<PricingPlan>[]> | undefined) => {
  return (
    pricingPlans?.find(
      (pricingPlanObj) => pricingPlanObj?.pricingPlanType === PricingPlanTypeDisplay.DEFAULT_PRICING
    ) ??
    pricingPlans?.find((pricingPlanObj) => pricingPlanObj?.pricingPlanType === PricingPlanTypeDisplay.ADVANTAGE_PRICING)
  );
};
/**
 * 1. Find plans having commercial type with active status
 * 2. From the list above, select a plan having default pricing and fallback advantage pricing plan type
 * 3. If no plan is available, select a default pricing and fallback advantage pricing plan type from entire plan list
 * @param pricingPlans
 */
export const getDefaultPricingPlan = (pricingPlans: Maybe<Maybe<PricingPlan>[]> | undefined) => {
  if (pricingPlans?.length) {
    const commercialActivePricingPlans = pricingPlans.filter(
      (pricingPlanObj) =>
        // Modified the AC implementation to include only ACTIVE plans
        pricingPlanObj?.type === PricingPlanType.COMMERCIAL && pricingPlanObj?.status === PricingPlanStatus.ACTIVE
    );
    if (commercialActivePricingPlans.length) {
      const defaultPricingPlan = getAppropriatePricingPlanBasedOnPlanType(commercialActivePricingPlans);

      if (defaultPricingPlan) return defaultPricingPlan;
      return commercialActivePricingPlans[0];
    }
    return getAppropriatePricingPlanBasedOnPlanType(pricingPlans);
  }

  return null;
};

export const getAvailableActiveOfferingOptions = (
  availableOfferings: Offering[],
  isCompassPremiumEnabled?: boolean,
  isJPDPremiumOfferingEnabled?: boolean,
  isAppEditionSupportEnabled?: boolean
): OfferingSelectItem[] => {
  return defaultTo(availableOfferings, [])
    .filter(
      (offering: Offering) =>
        offering.status === 'ACTIVE' &&
        isOfferingIncluded(offering as ProductOffering, {
          isEnterpriseOfferingEnabled: false,
          isJSMRepackagingEnabled: false,
          isCompassPremiumEnabled,
          isJPDPremiumOfferingEnabled,
          isAppEditionSupportEnabled,
        })
    )
    .sort((offering1, offering2) => {
      // sort plans in terms of level in asc. order
      if (offering1.level && offering2.level) {
        return offering1.level - offering2.level;
      }
      return 0;
    })
    .map((offering: Offering) => ({
      label: offering.name,
      value: offering.id,
    }));
};
